import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Building2, Loader2 } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRICULA = ["International Baccalaureate (IB)", "Cambridge", "ICSE", "CBSE", "State Board"];
const DESIGNATIONS = ["Principal/Vice Principal", "Coordinator", "Career Counsellor", "Teacher"];

function errText(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function SchoolRegister() {
  const nav = useNavigate();
  const [f, setF] = useState({
    school_name: "", curriculum: "", address: "", official_name: "",
    designation: "", email: "", phone: "", password: "",
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${BASE}/school/register`, f);
      localStorage.setItem("cc_school_token", data.token);
      localStorage.setItem("cc_school", JSON.stringify(data.school));
      nav("/school/dashboard");
    } catch (e2) {
      setErr(errText(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-[#16244F] outline-none focus:ring-2 focus:ring-[#F58220]";
  const label = "block text-sm font-semibold text-[#16244F] mb-1.5";

  return (
    <div className="min-h-screen bg-[#f7f8fb]">
      <Header />
      <main className="max-w-[720px] mx-auto px-5 py-12">
        <div className="bg-white rounded-2xl cc-shadow-card p-7 lg:p-9">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#16244F] grid place-items-center text-white">
              <Building2 size={22} />
            </div>
            <div>
              <h1 className="font-head text-2xl font-bold text-[#16244F]">School / College Registration</h1>
              <p className="text-sm text-[#8a90a0]">Create an institutional account to view your students' reports.</p>
            </div>
          </div>

          {err && <div className="mb-4 text-sm text-red-600 bg-red-50 rounded-lg px-4 py-3" data-testid="school-register-error">{err}</div>}

          <form onSubmit={submit} className="space-y-4" data-testid="school-register-form">
            <div>
              <label className={label}>School / College Name</label>
              <input className={input} value={f.school_name} onChange={set("school_name")} required data-testid="field-school-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Curriculum</label>
                <select className={input} value={f.curriculum} onChange={set("curriculum")} required data-testid="field-curriculum">
                  <option value="">Select curriculum</option>
                  {CURRICULA.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className={label}>Designation</label>
                <select className={input} value={f.designation} onChange={set("designation")} required data-testid="field-designation">
                  <option value="">Select designation</option>
                  {DESIGNATIONS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={label}>Address</label>
              <textarea className={input} rows={2} value={f.address} onChange={set("address")} required data-testid="field-address" />
            </div>
            <div>
              <label className={label}>Name of the Official</label>
              <input className={input} value={f.official_name} onChange={set("official_name")} required data-testid="field-official-name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={label}>Official Email ID</label>
                <input type="email" className={input} value={f.email} onChange={set("email")} required data-testid="field-email" />
              </div>
              <div>
                <label className={label}>Mobile Number</label>
                <input className={input} value={f.phone} onChange={set("phone")} required data-testid="field-phone" />
              </div>
            </div>
            <div>
              <label className={label}>Password</label>
              <input type="password" className={input} value={f.password} onChange={set("password")} required minLength={6} data-testid="field-password" />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="school-register-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#F58220] hover:bg-[#e0730f] text-white font-head font-semibold text-sm px-5 py-3 rounded-lg transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />} Create Account
            </button>
          </form>

          <p className="text-sm text-[#8a90a0] mt-5 text-center">
            Already registered? <Link to="/school/login" className="font-semibold text-[#F58220]" data-testid="to-school-login">School Login</Link>
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
